import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  DockerSetupFormData,
  DockerSetupPolicyPayload,
  DOCKER_SETUP_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is DockerSetupPolicyPayload
 */
function isDockerSetupPolicyPayload(
  data: unknown
): data is DockerSetupPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'enableHostManagementFeatures' in data
  );
}

/**
 * Hook to get initial values for Docker Setup Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useDockerSetupInitialValues(
  policy?: TypedPolicy<unknown>
): DockerSetupFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return DOCKER_SETUP_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isDockerSetupPolicyPayload(data)) {
      return DOCKER_SETUP_INITIAL_VALUES;
    }

    return {
      enableHostManagementFeatures:
        data.enableHostManagementFeatures?.value ?? false,
      allowVolumeBrowserForRegularUsers:
        data.allowVolumeBrowserForRegularUsers?.value ?? false,
      enableGpuManagement: data.enableGpuManagement?.value ?? false,
      enableImageNotification: data.enableImageNotification?.value ?? false,
      gpus: data.gpus?.value ?? [],
      changeWindow: data.endpointChangeWindow?.value ?? {
        Enabled: false,
        StartTime: '22:00',
        EndTime: '02:00',
      },
    };
  }, [policy]);
}
